import React, { useState } from "react";

import { IDashboardComponent } from "api/model/dashboard/DashboardComponent";
import { DashboardComponentGroup } from "api/model/dashboard/DashboardComponentGroup";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { confirmPopup } from "primereact/confirmpopup";
import { DataTable, DataTableRowReorderEvent } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { T } from "services/lang/LanguageService";
import ComponentConfiguration from "./ComponentConfiguration";

type ComponentSubTableEditorProps = {
  group: DashboardComponentGroup;
  onChange: (group: DashboardComponentGroup, index: number) => void;
  groupIndex: number;
};

const ComponentSubTableEditor: React.FC<ComponentSubTableEditorProps> = ({ group, groupIndex, onChange }) => {
  const onRowReorder = (e: DataTableRowReorderEvent<IDashboardComponent[]>) => {
    group.components = e.value;
    onChange(group, groupIndex);
  };

  const onRemoveComponentClick = (index: number) => {
    group.components.splice(index, 1);
    onChange(group, groupIndex);
  };

  const confirmComponentDelete = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    confirmPopup({
      target: event.currentTarget,
      message: T("confirm-popup.do-you-want-to-delete-this-component"),
      icon: "pi pi-info-circle",
      acceptClassName: "p-button-danger",
      accept: () => onRemoveComponentClick(index),
    });
  };

  const editButton = (rowData: IDashboardComponent, index: number) => (
    <EditButton rowData={rowData} index={index} group={group} groupIndex={groupIndex} onChange={onChange} />
  );

  const removeButton = (index: number) => (
    <Button
      icon="pi pi-trash"
      className="p-button-sm p-button-danger"
      onClick={event => confirmComponentDelete(event, index)}
    />
  );

  return (
    <div className="group-table">
      <DataTable
        value={group.components}
        size="small"
        editMode="row"
        reorderableRows
        onRowReorder={onRowReorder}
      >
        <Column
          field="title"
          header={T("word.components")}
          body={data => (data.title && data.title !== "" ? data.title : "-")}
        />
        <Column field="type" header={T("word.type")} />
        <Column style={{ width: "0" }} body={(rowData, options) => editButton(rowData, options.rowIndex)} />
        <Column style={{ width: "0" }} body={(_, options) => removeButton(options.rowIndex)} />
        <Column rowReorder style={{ width: "3em" }} />
      </DataTable>
    </div>
  );
};

export default ComponentSubTableEditor;

const EditButton: React.FC<
  ComponentSubTableEditorProps & { rowData: IDashboardComponent; index: number }
> = ({ group, groupIndex, onChange, index, rowData }) => {
  const [dialog, setDialog] = useState(false);

  const onComponentChange = (newComponent: IDashboardComponent) => {
    group.components[index] = newComponent;
    onChange(group, groupIndex);
  };

  return (
    <>
      <Button icon="pi pi-cog" className="p-button-sm" onClick={() => setDialog(true)} />
      <Dialog
        visible={dialog}
        onHide={() => setDialog(false)}
        header={`${rowData.title || ""} ${T("word.component-configuration")}`}
        style={{ width: "min(100%, 1000px)" }}
      >
        <ComponentConfiguration
          component={rowData}
          onChange={onComponentChange}
          closeDialog={() => setDialog(false)}
        />
      </Dialog>
    </>
  );
};
